import { nextTick, ref, watch } from 'vue';

const useTooltipPosition = (props) => {
  const tooltipWrapper = ref<HTMLDivElement>();
  const tooltipPosition = ref({
    left: '0px',
    top: '0px',
  });

  const DISTANCE_TOOLTIP_AND_CURSOR = 20;
  // 앵커(행/바) 기준 배치 시 바와 툴팁 사이 간격 — 커서 간격(20)보다 작게 두어 "바로 아래/위"에 붙도록.
  const TOOLTIP_ANCHOR_GAP = 4;
  const setTooltipPosition = () => {
    let left;
    let top;

    if (props?.position) {
      left = props.position?.left;
      top = props.position?.top;
    } else if (props?.evt) {
      left = props.evt?.pageX;
      top = props.evt?.pageY;
    }

    const tooltipWrapperSize = tooltipWrapper.value?.getBoundingClientRect();
    if (tooltipWrapperSize) {
      const { width: wrapperWidth, height: wrapperHeight } = tooltipWrapperSize;

      const { scrollLeft, scrollTop, clientWidth } = document.documentElement;

      const expectedPosX = left + DISTANCE_TOOLTIP_AND_CURSOR;
      const maximumPosX = clientWidth + scrollLeft - wrapperWidth - DISTANCE_TOOLTIP_AND_CURSOR;
      if (expectedPosX > maximumPosX) {
        const reversedPosX = left - (wrapperWidth + DISTANCE_TOOLTIP_AND_CURSOR);
        left = Math.max(reversedPosX, 0);
      } else {
        left = expectedPosX;
      }

      // position.height(앵커=행/바 높이) 지정 시 top(앵커 상단) 아래로 배치. 미지정(커서 기준)이면 height=0 → 기존 동작.
      // 앵커(바) 기준일 땐 바로 아래/위에 붙도록 작은 간격, 커서 기준일 땐 기존 간격(커서 가림 방지).
      const anchorHeight = props?.position?.height ?? 0;
      const gapY = anchorHeight > 0 ? TOOLTIP_ANCHOR_GAP : DISTANCE_TOOLTIP_AND_CURSOR;
      const expectedPosY = top + anchorHeight + gapY;
      const maximumPosY =
        document.body.clientHeight + scrollTop - wrapperHeight - DISTANCE_TOOLTIP_AND_CURSOR;
      if (expectedPosY > maximumPosY) {
        // 하단 공간 부족 → 위로 flip. gapY 는 height=0(커서)일 때 DISTANCE 라 기존 동작과 동일하게 유지된다.
        top = Math.max(top - wrapperHeight - gapY, 0);
      } else {
        top = expectedPosY;
      }
    }

    tooltipPosition.value = {
      left: `${left}px`,
      top: `${top}px`,
    };
  };

  watch(
    () => [props.position, props.evt],
    async () => {
      if (!props.isShow) {
        return;
      }

      await nextTick();
      setTooltipPosition();
    },
    { immediate: true },
  );

  return {
    tooltipWrapper,
    tooltipPosition,
  };
};

const useTooltipModel = () => {
  const mouseoverEvt = ref<MouseEvent | null>(null);
  const getDynamicEvtType = (isNotUsedEvt = true) => {
    if (!isNotUsedEvt) {
      return {
        mouseoverEvtName: 'mouseover',
        mousemoveEvtName: 'mousemove',
        mouseleaveEvtName: 'mouseleave',
      };
    }
    return {
      mouseoverEvtName: null,
      mousemoveEvtName: null,
      mouseleaveEvtName: null,
    };
  };
  const onMouseover = (e: MouseEvent) => {
    mouseoverEvt.value = e;
  };
  const onMouseleave = () => {
    mouseoverEvt.value = null;
  };

  return {
    mouseoverEvt,
    getDynamicEvtType,
    onMouseover,
    onMouseleave,
  };
};

export { useTooltipModel, useTooltipPosition };
